import type { ItemType } from 'antd/es/menu/interface';
import {
  type FC,
  type PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useNodes, useReactFlow } from '@xyflow/react';
import type { MenuInfo } from 'rc-menu/lib/interface';
import {
  AddRemoveButtons,
  DropdownItem,
} from '../../../../../common/components';
import type { UUIDType } from '../../../../../common/utils';
import {
  getDefaultAlgorithmMappingNode,
  getDefaultAlgorithmSourceNode,
  getDefaultConstantNode,
} from '../../schema/defaultValues';
import type {
  AlgorithmJoinsAndMappingsGraphNodeOfType,
  AlgorithmJoinsAndMappingsGraphNodeType,
  AlgorithmJoinsAndMappingsGraphValidFormDataType,
} from '../../schema/types';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { AlgorithmJoinsWithMappingsGraphContext } from '../AlgorithmJoinsWithMappingsGraphContext';
import translations from './AddRemoveAlgorithmNodeButtons.translations.json';
import { AlgorithmType } from '../../../schema/zAlgorithmType';

const { addTranslations, useTranslation } = i18nNsFactory(
  'add-remove-algorithm-source-node-buttons',
);
addTranslations(translations);

export interface AddRemoveAlgorithmSourceNodeButtonsProps
  extends PropsWithChildren {
  nodeId: UUIDType;
  nodeIndex: number;
}

export const AddRemoveAlgorithmNodeButtons: FC<
  AddRemoveAlgorithmSourceNodeButtonsProps
> = (props) => {
  const { nodeId, nodeIndex } = props;
  const { t } = useTranslation();
  const { screenToFlowPosition, deleteElements, getNodes } = useReactFlow();

  const { setValue } =
    useFormContext<AlgorithmJoinsAndMappingsGraphValidFormDataType>();
  const typeCode = useWatch({ name: 'typeCode' });

  const addNode = useCallback(
    (info: MenuInfo, node: AlgorithmJoinsAndMappingsGraphNodeType) => {
      if ('clientX' in info.domEvent && 'clientY' in info.domEvent) {
        node.position = screenToFlowPosition({
          x: info.domEvent.clientX,
          y: info.domEvent.clientY,
        });
      }
      const index = getNodes().length;
      setValue(`nodes.${index}`, node, { shouldDirty: true });
    },
    [getNodes, setValue, screenToFlowPosition],
  );

  const removeThisNode = useCallback(() => {
    // It is better to do this via `deleteElements` (rather than form.setValue) because
    // it "handles deleting any connected edges as well as child nodes".
    // Corresponding changes to form state will propagate through main Graph component's
    // `onNodesDelete` and `onEdgesDelete` handlers.
    const nodes = getNodes();
    void deleteElements({
      nodes: nodes.filter((_, index) => index === nodeIndex),
    });
  }, [deleteElements, getNodes, nodeIndex]);

  const { algorithmSourcesById } = useContext(
    AlgorithmJoinsWithMappingsGraphContext,
  );
  const nodes =
    useNodes<AlgorithmJoinsAndMappingsGraphNodeOfType<'algorithmSource'>>();

  const canAddAlgorithmSourceNodes = useMemo(() => {
    const algorithmSourceNodes = nodes.filter(
      (node) => node.type === 'algorithmSource',
    );
    return algorithmSourceNodes.length < algorithmSourcesById.size;
  }, [algorithmSourcesById, nodes]);

  const menu = useMemo(() => {
    var items: ItemType[] = [];
    if (typeCode === AlgorithmType.INSERT) {
      items.push({
        key: 'add-join',
        label: <DropdownItem>{t('menu.add-join')}</DropdownItem>,
        onClick: (info) => {
          addNode(info, getDefaultAlgorithmSourceNode());
        },
        disabled: !canAddAlgorithmSourceNodes,
      });
    }
    items.push({
      key: 'add-mapping',
      label: <DropdownItem>{t('menu.add-mapping')}</DropdownItem>,
      onClick: (info) => {
        addNode(info, getDefaultAlgorithmMappingNode());
      },
    });
    items.push({
      key: 'add-constant',
      label: <DropdownItem>{t('menu.add-constant')}</DropdownItem>,
      onClick: (info) => {
        addNode(info, getDefaultConstantNode());
      },
    });
    return { items };
  }, [addNode, canAddAlgorithmSourceNodes, t, typeCode]);

  return (
    <AddRemoveButtons
      id={`node-${nodeId}`}
      addMenu={menu}
      onClickRemove={removeThisNode}
    />
  );
};
